import type { EmployeePayType } from '@/types/database';

/** Standard paid workdays per year used to convert annual salary → daily rate. */
export const PTO_PAYOUT_WORKDAYS_PER_YEAR = 260;

/** Hours in a standard PTO day when converting hourly wage → daily rate. */
export const PTO_PAYOUT_HOURS_PER_DAY = 8;

/** Company pays unused-PTO payout within this many days after cycle end. */
export const PTO_PAYOUT_WINDOW_DAYS = 30;

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function toISODate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Build an anniversary date in `year` matching the month/day of `start`.
 * Clamps Feb 29 → Feb 28 in non-leap years.
 */
function anniversaryInYear(start: Date, year: number): Date {
  const month = start.getMonth();
  const day = start.getDate();
  const candidate = new Date(year, month, day);
  if (candidate.getMonth() !== month) {
    // Overflowed (e.g. Feb 29 → Mar 1) — use last day of the target month.
    return new Date(year, month + 1, 0);
  }
  return candidate;
}

/**
 * Next personal PTO cycle end: the next anniversary of employment start
 * that is on or after `asOf` (so a cycle ending today shows 0 days left).
 */
export function ptoCycleEndDate(
  startDateISO: string,
  asOf: Date = new Date(),
): Date {
  const start = parseLocalDate(startDateISO);
  const today = startOfLocalDay(asOf);
  let end = anniversaryInYear(start, today.getFullYear());
  if (end < today) {
    end = anniversaryInYear(start, today.getFullYear() + 1);
  }
  return end;
}

/** Whole calendar days from `asOf` until `target` (0 if same day or past). */
export function calendarDaysUntil(
  target: Date,
  asOf: Date = new Date(),
): number {
  const a = startOfLocalDay(asOf).getTime();
  const b = startOfLocalDay(target).getTime();
  if (b <= a) return 0;
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

/** Daily pay in cents used for unused-PTO payout estimates. */
export function dailyPayRateCents(
  payRateCents: number | null | undefined,
  payType: EmployeePayType | null | undefined,
): number | null {
  if (payRateCents == null || payRateCents < 0) return null;
  if (payType === 'salary') {
    return Math.round(payRateCents / PTO_PAYOUT_WORKDAYS_PER_YEAR);
  }
  // Default / hourly
  return Math.round(payRateCents * PTO_PAYOUT_HOURS_PER_DAY);
}

/**
 * Potential pre-tax payout for unused PTO days at the employee's current rate.
 * Returns null when pay is not configured.
 */
export function potentialPtoPayoutCents(
  remainingPtoDays: number,
  payRateCents: number | null | undefined,
  payType: EmployeePayType | null | undefined,
): number | null {
  const daily = dailyPayRateCents(payRateCents, payType);
  if (daily == null) return null;
  const days = Math.max(0, Number(remainingPtoDays) || 0);
  return Math.round(daily * days);
}

/** Date by which the company aims to pay the unused-PTO payout. */
export function ptoPayoutDueDate(cycleEnd: Date): Date {
  const due = new Date(cycleEnd);
  due.setDate(due.getDate() + PTO_PAYOUT_WINDOW_DAYS);
  return due;
}

export function formatISODateLocal(d: Date): string {
  return toISODate(d);
}
